import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import useGameSounds from "../../../hooks/useGameSounds";
import useKidVoice from "../../../hooks/useKidVoice";
import useGameContent from "../../../hooks/useGameContent";
import GameShell from "../../../components/Kids/GameShell";
import Button from "../../../components/Kids/Button";
import EmptyState from "../../../components/Kids/EmptyState";
import Skeleton from "../../../components/Kids/Skeleton";
import Celebration from "../../../components/Kids/Celebration";

const API   = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("kids_parent_token");

// README game-identity gradient — Typing · Alphabet (LIGHT theme)
const TYPING_BG = "linear-gradient(180deg, #FFF8F2 0%, #FFE9D6 60%, #FFD9BF 100%)";

const ALPHABET = [
  { letter:"A", emoji:"🍎", word:"Apple" },
  { letter:"B", emoji:"🦋", word:"Butterfly" },
  { letter:"C", emoji:"🐱", word:"Cat" },
  { letter:"D", emoji:"🐶", word:"Dog" },
  { letter:"E", emoji:"🥚", word:"Egg" },
  { letter:"F", emoji:"🐟", word:"Fish" },
  { letter:"G", emoji:"🍇", word:"Grapes" },
  { letter:"H", emoji:"🏠", word:"House" },
  { letter:"I", emoji:"🍦", word:"Ice Cream" },
  { letter:"J", emoji:"🕹️",  word:"Joystick" },
  { letter:"K", emoji:"🪁", word:"Kite" },
  { letter:"L", emoji:"🦁", word:"Lion" },
  { letter:"M", emoji:"🌙", word:"Moon" },
  { letter:"N", emoji:"🌱", word:"Nature" },
  { letter:"O", emoji:"🍊", word:"Orange" },
  { letter:"P", emoji:"🐧", word:"Penguin" },
  { letter:"Q", emoji:"👸", word:"Queen" },
  { letter:"R", emoji:"🌈", word:"Rainbow" },
  { letter:"S", emoji:"☀️", word:"Sun" },
  { letter:"T", emoji:"🐯", word:"Tiger" },
  { letter:"U", emoji:"☂️", word:"Umbrella" },
  { letter:"V", emoji:"🌋", word:"Volcano" },
  { letter:"W", emoji:"🐳", word:"Whale" },
  { letter:"X", emoji:"🎸", word:"Xylophone" },
  { letter:"Y", emoji:"🪁", word:"Yo-yo" },
  { letter:"Z", emoji:"🦓", word:"Zebra" },
];

const LIVES = 3;
const ROUNDS_PER_GAME = 10;
const PRAISE = ["Great job", "Awesome", "Well done", "You got it", "Super", "Fantastic", "Yay"];

// Built-in typing game. Admin-created typing games supply their own
// content.deck = [{ letter, emoji, word }, ...].
const VEHICLE_DECK = [
  { letter: "C", emoji: "🚗", word: "Car" },
  { letter: "B", emoji: "🚌", word: "Bus" },
  { letter: "T", emoji: "🚋", word: "Tram" },
  { letter: "J", emoji: "🚤", word: "Jet Ski" },
  { letter: "T", emoji: "🚚", word: "Truck" },
  { letter: "K", emoji: "🛶", word: "Kayak" },
  { letter: "Y", emoji: "🛳️", word: "Yacht" },
  { letter: "T", emoji: "🚜", word: "Tractor" },
  { letter: "G", emoji: "✈️", word: "Glider" },
  { letter: "S", emoji: "🛵", word: "Scooter" },
  { letter: "L", emoji: "🏗️", word: "Loader" },
  { letter: "B", emoji: "🚲", word: "Bicycle" },
  { letter: "M", emoji: "🚝", word: "Monorail" },
  { letter: "R", emoji: "🚀", word: "Rocket" },
  { letter: "H", emoji: "🚁", word: "Helicopter" },
];

const FOOD_DECK = [
  { letter: "A", emoji: "🍎", word: "Apple" },
  { letter: "B", emoji: "🍌", word: "Banana" },
  { letter: "C", emoji: "🥕", word: "Carrot" },
  { letter: "G", emoji: "🍇", word: "Grapes" },
  { letter: "K", emoji: "🥝", word: "Kiwi" },
  { letter: "L", emoji: "🍋", word: "Lemon" },
  { letter: "M", emoji: "🥭", word: "Mango" },
  { letter: "O", emoji: "🍊", word: "Orange" },
  { letter: "P", emoji: "🍑", word: "Peach" },
  { letter: "P", emoji: "🍐", word: "Pear" },
  { letter: "C", emoji: "🌽", word: "Corn" },
  { letter: "O", emoji: "🧅", word: "Onion" },
  { letter: "P", emoji: "🥔", word: "Potato" },
  { letter: "T", emoji: "🍅", word: "Tomato" },
  { letter: "G", emoji: "🧄", word: "Garlic" },
];

const TYPING_HARDCODED = {
  "alphabet-adventure": { deck: ALPHABET },
  "vehicle-speller":    { deck: VEHICLE_DECK },
  "food-speller":       { deck: FOOD_DECK },
};

export default function TypingGame() {
  const { slug }  = useParams();
  const [sp]      = useSearchParams();
  const navigate  = useNavigate();
  const sounds    = useGameSounds();
  const { speak, cancel } = useKidVoice();
  const { t }     = useTranslation();

  const gameId   = sp.get("gameId");
  const childId  = sp.get("childId") || sessionStorage.getItem("childId");
  const childName= sessionStorage.getItem("childName") || "Friend";

  const { game, resolving, notFound } = useGameContent({
    slug, gameId, hardcoded: TYPING_HARDCODED, defaults: { deck: ALPHABET },
  });
  const deckSource = (game?.deck && game.deck.length) ? game.deck : ALPHABET;

  const [deck,     setDeck]    = useState([]);
  const [idx,      setIdx]     = useState(0);
  const [input,    setInput]   = useState("");
  const [lives,    setLives]   = useState(LIVES);
  const [score,    setScore]   = useState(0);
  const [phase,    setPhase]   = useState("welcome");   // welcome|playing|feedback|complete
  const [correct,  setCorrect] = useState(null);
  const [wobble,   setWobble]  = useState(false);
  const [reward,   setReward]  = useState(null);
  const [levelUpSeen, setLevelUpSeen] = useState(false);
  const inputRef  = useRef(null);
  const startTime = useRef(Date.now());

  // Build the playing deck once the game's content is resolved.
  useEffect(() => {
    if (deckSource.length) setDeck(shuffle([...deckSource]).slice(0, ROUNDS_PER_GAME));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game]);

  useEffect(() => {
    if (!gameId || !childId) return;
    axios.post(`${API}/api/kids/games/${childId}/start/${gameId}`, {}, {
      headers: { Authorization: `Bearer ${token()}` }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (phase === "playing" && inputRef.current) inputRef.current.focus();
  }, [idx, phase]);

  useEffect(() => () => cancel(), [cancel]); // stop any speech on unmount

  // Kick off the game from the welcome screen (also unlocks audio on the user gesture)
  const beginGame = () => {
    setPhase("playing");
    startTime.current = Date.now();
    sounds.playCorrect();   // clear, audible start chime (also unlocks audio)
    speak(`Let's go ${childName}! Type the letters as fast as you can!`);
  };

  const speakWelcome = useCallback(() => {
    speak(`Hi ${childName}! Welcome to Alphabet Adventure! Let's learn our letters together. Are you ready?`);
  }, [childName, speak]);

  // Lets the user verify audio is working: plays a clear chime AND a spoken line.
  const testSound = () => {
    sounds.playCorrect();
    speak(`Sound is working! Let's play, ${childName}!`);
  };

  const advance = (wasCorrect) => {
    const next = idx + 1;
    if (next >= deck.length) {
      endGame(wasCorrect ? score + 1 : score);
    } else {
      setIdx(next);
      setInput("");
      setPhase("playing");
      setCorrect(null);
    }
  };

  const resolveAnswer = (ok) => {
    setCorrect(ok);
    setPhase("feedback");
    if (ok) {
      setScore(s => s + 1);
      sounds.playCorrect();
      speak(`${PRAISE[Math.floor(Math.random() * PRAISE.length)]}!`);
    } else {
      sounds.playWrong();
      setWobble(true);
      setTimeout(() => setWobble(false), 500);
      speak(`Oops! That was the letter ${deck[idx].letter}.`);
      setLives(lv => {
        const newL = lv - 1;
        if (newL <= 0) { setTimeout(() => endGame(score), 1200); }
        return newL;
      });
    }
    setTimeout(() => {
      if (ok || lives > 1) advance(ok);
    }, ok ? 900 : 1200);
  };

  const endGame = async (finalScore) => {
    setPhase("complete");
    const pct     = Math.round((finalScore / deck.length) * 100);
    const elapsed = Math.round((Date.now() - startTime.current) / 1000);
    sounds.playComplete(pct >= 90);
    speak(pct >= 90
      ? `Amazing work ${childName}! You are a letter superstar!`
      : `Great effort ${childName}! Let's play again to learn even more!`);
    if (!gameId || !childId) return;
    try {
      const res = await axios.post(
        `${API}/api/kids/games/${childId}/complete/${gameId}`,
        { score: pct, time_spent_seconds: elapsed },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setReward(res.data?.data);
    } catch {}
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && phase === "playing" && input) {
      resolveAnswer(input.toUpperCase().trim() === deck[idx].letter);
    }
  };

  const handleLetter = (l) => {
    if (phase !== "playing") return;
    setInput(l);
    resolveAnswer(l.toUpperCase() === deck[idx].letter);
  };

  const replay = () => {
    setDeck(shuffle([...deckSource]).slice(0, ROUNDS_PER_GAME));
    setIdx(0); setScore(0); setLives(LIVES); setInput("");
    setCorrect(null); setReward(null); setLevelUpSeen(false);
    setPhase("playing");
    startTime.current = Date.now();
    sounds.playClick();
    speak(`Here we go again ${childName}!`);
  };

  const current = deck[idx];

  /* ── LOADING / NOT READY ── */
  if (resolving) return (
    <GameShell title="Loading…" emoji="🔤" theme="light" bg={TYPING_BG} onBack={() => navigate(-1)}>
      <div className="flex gap-1 mb-4">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-1.5 flex-1" rounded="9999px" />
        ))}
      </div>
      <Skeleton className="h-56 w-full max-w-xs mx-auto mb-6" rounded="20px" />
      <div className="w-full max-w-xs mx-auto space-y-1.5">
        <Skeleton className="h-10 w-full" rounded="10px" />
        <Skeleton className="h-10 w-full" rounded="10px" />
        <Skeleton className="h-10 w-3/4 mx-auto" rounded="10px" />
      </div>
    </GameShell>
  );
  if (notFound || !deckSource.length) return (
    <div className="min-h-[100dvh] flex items-center justify-center text-center p-4" style={{ background: TYPING_BG }}>
      <EmptyState
        theme="light"
        mood="thinking"
        title="Oops — no internet!"
        subtitle="This game couldn't load. Check your connection and try again."
        cta="Retry"
        onCta={() => window.location.reload()}
      />
    </div>
  );

  /* ── WELCOME ── */
  if (phase === "welcome") {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center p-4 select-none overflow-hidden relative" style={{ background: TYPING_BG }}>
        <style>{`
          @keyframes pop { 0%{transform:scale(0)} 70%{transform:scale(1.08)} 100%{transform:scale(1)} }
          @keyframes floaty { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
          @keyframes wave { 0%,100%{transform:rotate(0)} 25%{transform:rotate(18deg)} 75%{transform:rotate(-12deg)} }
          .pop{animation:pop .5s cubic-bezier(.34,1.56,.64,1)} .floaty{animation:floaty 3s ease-in-out infinite} .wave{animation:wave 1.2s ease-in-out infinite;transform-origin:70% 70%}
        `}</style>
        {["🎈","⭐","🌈","🪁","✨","☁️"].map((e, i) => (
          <span key={i} className="absolute text-3xl floaty opacity-70"
            style={{ insetInlineStart:`${10 + i*15}%`, top:`${12 + (i%3)*22}%`, animationDelay:`${i*0.4}s` }}>{e}</span>
        ))}
        <div className="bg-white rounded-sheet-lg p-8 max-w-sm w-full text-center shadow-sheet pop relative z-10">
          <div className="text-7xl mb-2 wave inline-block">👋</div>
          <h1 className="text-2xl text-ink font-display font-semibold">Hi {childName}!</h1>
          <p className="text-[#EE5F2B] font-display font-semibold text-lg mt-1">🔤 Alphabet Adventure</p>
          <p className="text-muted text-sm mt-3 mb-6">
            Tap the right letters to match the picture. Listen to the cheers and have fun learning your ABCs!
          </p>
          <Button onClick={beginGame} className="w-full !text-lg !py-4">
            Let's Play! 🚀
          </Button>
          <button onClick={testSound}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-body font-semibold text-muted hover:text-[#EE5F2B] border border-cardline rounded-full px-4 py-2 transition">
            🔊 Test Sound
          </button>
          <p className="text-[11px] text-faint mt-2">No sound? Check your volume — tap Test Sound to try.</p>
        </div>
      </div>
    );
  }

  /* ── COMPLETE ── */
  if (phase === "complete") {
    const pct = Math.round((score / deck.length) * 100);
    const perfect = pct >= 90;
    return (
      <div className="min-h-[100dvh] flex items-center justify-center p-4" style={{ background: TYPING_BG }}>
        <style>{`@keyframes pp{0%{transform:scale(0)}70%{transform:scale(1.1)}100%{transform:scale(1)}}.pp{animation:pp 0.5s cubic-bezier(.34,1.56,.64,1)}`}</style>

        {reward?.level_up && !levelUpSeen && (
          <Celebration
            variant="level"
            title="Level Up!"
            subtitle={`You're now Level ${reward.level}!`}
            cta="Continue"
            onContinue={() => setLevelUpSeen(true)}
          />
        )}

        <div className="bg-white rounded-card-lg p-8 max-w-sm w-full text-center shadow-sheet pp">
          <div className="text-7xl mb-3">{perfect ? "🏆" : score >= deck.length/2 ? "🌟" : "💪"}</div>
          <h2 className="text-2xl text-ink mb-1 font-display font-semibold">
            {perfect ? "Amazing!" : score >= deck.length/2 ? "Great job!" : "Keep practising!"}
          </h2>
          <p className="text-muted mb-5">{childName} got {score}/{deck.length} letters right</p>

          <div className="relative w-28 h-28 mx-auto mb-5">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f3e8df" strokeWidth="3"/>
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#EE5F2B" strokeWidth="3"
                strokeDasharray={`${pct} 100`} strokeLinecap="round"/>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-display font-semibold text-ink">{pct}%</span>
            </div>
          </div>

          {reward && (
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[["⭐","XP",`+${reward.xp_earned}`],["🪙","Coins",`+${reward.coins_earned}`],["🔥","Streak",`${reward.streak_days}d`]].map(([i,l,v])=>(
                <div key={l} className="bg-orange-50 rounded-xl p-2 text-center">
                  <div className="text-xl">{i}</div>
                  <div className="font-extrabold text-sm text-[#EE5F2B]">{v}</div>
                  <div className="text-xs text-faint">{l}</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={replay}
              className="flex-1 bg-faint/20 hover:bg-faint/30 text-ink font-display font-semibold py-3 rounded-2xl transition">
              🔄 {t("kids.games.typing.playAgain")}
            </button>
            <button onClick={() => navigate(-1)}
              className="flex-1 bg-[#EE5F2B] hover:bg-brand-hover text-white font-display font-semibold py-3 rounded-2xl shadow-primary transition">
              🎮 More
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── PLAYING ── */
  return (
    <GameShell
      title="Alphabet Adventure"
      emoji="🔤"
      theme="light"
      bg={TYPING_BG}
      onBack={() => navigate(-1)}
      right={
        <div className="flex gap-1">
          {[...Array(LIVES)].map((_, i) => (
            <span key={i} className="text-xl">{i < lives ? "❤️" : "🤍"}</span>
          ))}
        </div>
      }
    >
      <style>{`
        @keyframes bounce-in { 0%{transform:scale(0) rotate(-10deg);opacity:0} 70%{transform:scale(1.15) rotate(3deg)} 100%{transform:scale(1) rotate(0);opacity:1} }
        @keyframes shake2 { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-12px)} 40%,80%{transform:translateX(12px)} }
        @keyframes correct-pulse { 0%{box-shadow:0 0 0 0 rgba(74,222,128,0.7)} 100%{box-shadow:0 0 0 24px rgba(74,222,128,0)} }
        .bounce-in { animation: bounce-in 0.4s cubic-bezier(.34,1.56,.64,1) }
        .shake2 { animation: shake2 0.4s ease }
        .correct-pulse { animation: correct-pulse 0.6s ease }
      `}</style>

      {/* Progress — brand-coloured segments (the shared ProgressBar is white-only,
          which has no contrast on this light theme, so it's rendered inline here
          matching the README's light-theme progress colours instead). */}
      <div className="flex gap-1 mb-1.5">
        {deck.map((_, i) => (
          <div key={i} className="flex-1 h-1.5 rounded-full transition-all"
            style={{ background: i < idx ? "#EE5F2B" : i === idx ? "#FBB03B" : "#F6D9C2" }} />
        ))}
      </div>
      <div className="text-center font-body font-bold text-[11px] text-faint mb-4">
        Letter {idx + 1} of {deck.length} · ⭐ {score} correct
      </div>

      {/* Main card */}
      <main className="flex-1 flex flex-col items-center justify-center pb-8">
        <div className={`bounce-in w-full max-w-xs ${wobble ? "shake2" : ""}`} key={idx}>
          {/* Big letter card */}
          <div className={`rounded-card-lg p-8 text-center mb-6 transition-all shadow-sheet ${
            correct === true  ? "bg-success correct-pulse"
            : correct === false ? "bg-danger"
            : "bg-white"
          }`}>
            <button onClick={() => speak(`${current.letter}. ${current.letter} is for ${current.word}.`)}
              className="text-7xl mb-3 active:scale-90 transition" title="Hear the word">{current.emoji}</button>
            <div className={`text-8xl font-display font-semibold leading-none mb-2 ${correct !== null ? "text-white" : "text-ink"}`}>
              {current.letter}
            </div>
            <div className={`text-lg font-body font-semibold ${correct !== null ? "text-white/80" : "text-muted"}`}>
              is for <span className="font-extrabold">{current.word}</span>
            </div>
            {correct === true  && <div className="text-3xl mt-3">🌟 {t("kids.games.typing.correct")}</div>}
            {correct === false && <div className="text-3xl mt-3">❌ {t("kids.games.typing.wrong")}</div>}
          </div>

          <p className="text-ink text-center font-body font-bold text-lg mb-5">
            Type the letter <span className="text-[#EE5F2B]">{current.letter}</span>
          </p>
        </div>

        {/* hidden input keeps physical-keyboard typing working */}
        <input ref={inputRef} value={input} onChange={() => {}} onKeyDown={handleKey}
          className="sr-only" aria-hidden="true" tabIndex={-1} />

        {/* On-screen keyboard (kid-friendly) */}
        <div className="w-full max-w-xs">
          {[
            ["Q","W","E","R","T","Y","U","I","O","P"],
            ["A","S","D","F","G","H","J","K","L"],
            ["Z","X","C","V","B","N","M"],
          ].map((row, ri) => (
            <div key={ri} className="flex justify-center gap-1.5 mb-1.5">
              {row.map(l => {
                const isTarget = phase === "playing" && l === current.letter;
                return (
                  <button key={l} onClick={() => handleLetter(l)}
                    style={{
                      background: phase !== "playing" ? "rgba(255,255,255,.5)" : isTarget ? "#EE5F2B" : "#fff",
                      boxShadow: phase !== "playing" ? "none" : isTarget ? "0 3px 0 #C6461E" : "0 2px 0 #EAD9C6",
                      color: phase !== "playing" ? "#B0A290" : isTarget ? "#fff" : "#2A2620",
                    }}
                    className="w-8 h-10 rounded-lg font-display font-semibold text-sm transition active:scale-90">
                    {l}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </main>
    </GameShell>
  );
}

function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
